import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface TeamWinner {
  sheetId: string;
  sheetName: string;
}

export interface VotingControlPanelProps {
  sessionId: string;
  activeVotes: any[];
  onRefresh: () => Promise<void>;
  projectionMode?: string;
  activeVotingTeam?: string | null;
  votingMode?: 'ALL_TOGETHER' | 'SEQUENTIAL';
  teamWinners?: Record<TeamColor, TeamWinner | null>;
  numPlayers?: number;
}

const TEAM_COLORS: Record<string, { bg: string; text: string }> = {
  BLUE: { bg: 'bg-blue-600', text: 'text-blue-200' },
  RED: { bg: 'bg-red-600', text: 'text-red-200' },
  GREEN: { bg: 'bg-green-600', text: 'text-green-200' },
  YELLOW: { bg: 'bg-yellow-600', text: 'text-yellow-200' },
};

const TEAM_NAMES: Record<TeamColor, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

const TEAM_ORDER: TeamColor[] = ['RED', 'GREEN', 'BLUE', 'YELLOW'];

export function VotingControlPanel({
  sessionId,
  activeVotes,
  onRefresh,
  projectionMode,
  activeVotingTeam,
  votingMode,
  teamWinners,
  numPlayers = 4,
}: VotingControlPanelProps) {
  // Filter teams based on numPlayers
  const activeTeams = TEAM_ORDER.slice(0, numPlayers);
  const [sheets, setSheets] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const hasActiveVotes = activeVotes.some((v: any) => v.isActive);
  const allClosed = activeVotes.length > 0 && !hasActiveVotes;
  const isResultsMode = projectionMode === 'sheet-vote-results' || projectionMode === 'sheet-vote-winners';

  // Fetch sheets and assignments
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all sheets
        const sheetsRes = await fetch('/api/sheets');
        if (sheetsRes.ok) {
          const sheetsData = await sheetsRes.json();
          setSheets(sheetsData.sheets || []);
        }

        // Fetch session assignments
        const assignRes = await fetch(`/api/sheets/session/${sessionId}/assignments`);
        if (assignRes.ok) {
          const assignData = await assignRes.json();
          console.log('[VotingControl] Assignments loaded:', assignData.assignments);
          setAssignments(assignData.assignments || []);
        }
      } catch (error) {
        console.error('Error fetching sheets/assignments:', error);
      }
    };

    fetchData();
  }, [sessionId]);

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Stato Votazioni</h3>
        <button
          onClick={onRefresh}
          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm font-semibold transition-all"
        >
          🔄 Aggiorna
        </button>
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        <span
          className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
            isResultsMode
              ? 'bg-purple-900 text-purple-300 border border-purple-600'
              : hasActiveVotes
              ? 'bg-green-900 text-green-300 border border-green-600'
              : 'bg-gray-700 text-gray-400'
          }`}
        >
          {isResultsMode ? '🏆 RISULTATI' : hasActiveVotes ? '🟢 ATTIVO' : '⚪ INATTIVO'}
        </span>
      </div>

      {/* SEQUENTIAL mode: Summary + Active Vote Details */}
      {votingMode === 'SEQUENTIAL' && teamWinners && (
        <div className="mb-4 space-y-4">
          {/* Summary of all teams */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-400 mb-2">Riepilogo Squadre</h4>
            {activeTeams.map((team) => {
              const winner = teamWinners[team];
              const isLive = activeVotes.some((v: any) => v.teamColor === team && v.isActive);
              const colors = TEAM_COLORS[team];

              return (
                <div
                  key={team}
                  className={`flex items-center justify-between p-2 rounded-lg ${colors.bg} bg-opacity-30`}
                >
                  <span className={`font-bold ${colors.text}`}>{TEAM_NAMES[team]}</span>
                  <span className="text-white">
                    {isLive ? (
                      <span className="text-yellow-400 animate-pulse">⏳ in corso...</span>
                    ) : winner ? (
                      <span className="text-green-400">✓ {winner.sheetName}</span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Detailed view for active team vote */}
          {(() => {
            const activeVote = activeVotes.find((v: any) => v.isActive && v.teamColor === activeVotingTeam);
            if (!activeVote) return null;

            const colors = TEAM_COLORS[activeVote.teamColor] || { bg: 'bg-gray-600', text: 'text-gray-200' };
            const totalSubmissions = activeVote.submissions?.length || 0;

            // Get all sheets assigned to this team
            const teamAssignments = assignments.filter((a: any) => a.teamColor === activeVote.teamColor);
            const assignedSheetIds = teamAssignments.map((a: any) => a.sheetId);
            const assignedSheets = sheets.filter((s: any) => assignedSheetIds.includes(s.id));

            // Count votes per sheet
            const votesBySheet: Record<string, number> = {};
            if (activeVote.submissions) {
              activeVote.submissions.forEach((sub: any) => {
                const key = sub.sheetId || 'random';
                votesBySheet[key] = (votesBySheet[key] || 0) + 1;
              });
            }

            const handleAddVote = async (sheetId: string) => {
              try {
                const response = await fetch(`/api/sheet-votes/${activeVote.id}/add-manual-vote`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ optionType: 'sheet', sheetId }),
                });
                if (response.ok) {
                  await onRefresh();
                  toast.success('Voto aggiunto!');
                }
              } catch (error) {
                console.error('Error adding vote:', error);
                toast.error('Errore aggiunta voto');
              }
            };

            const handleRemoveVote = async (sheetId: string) => {
              try {
                const submission = activeVote.submissions?.find((s: any) => s.sheetId === sheetId);
                if (!submission) {
                  toast.error('Nessun voto da rimuovere');
                  return;
                }

                const response = await fetch(`/api/sheet-votes/submissions/${submission.id}`, {
                  method: 'DELETE',
                });
                if (response.ok) {
                  await onRefresh();
                  toast.success('Voto rimosso!');
                }
              } catch (error) {
                console.error('Error removing vote:', error);
                toast.error('Errore rimozione voto');
              }
            };

            return (
              <div className="bg-gray-900 rounded-lg p-4 border border-yellow-500 ring-2 ring-yellow-500/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full ${colors.bg}`}></div>
                    <span className="text-white font-bold">Squadra {TEAM_NAMES[activeVote.teamColor as TeamColor] || activeVote.teamColor}</span>
                    <span className="bg-yellow-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold animate-pulse">
                      LIVE
                    </span>
                  </div>
                  <span className="text-gray-400 text-sm">{totalSubmissions} voti</span>
                </div>

                <div className="space-y-2">
                  {assignedSheets.map((sheet: any) => {
                    const count = votesBySheet[sheet.id] || 0;
                    const percentage = totalSubmissions > 0 ? (count / totalSubmissions) * 100 : 0;
                    const assignment = teamAssignments.find((a: any) => a.sheetId === sheet.id);

                    return (
                      <div key={sheet.id} className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-300">{sheet.name}</span>
                            {assignment?.isPrimary && (
                              <span className="text-yellow-400 text-xs">⭐</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">
                              {count} ({percentage.toFixed(0)}%)
                            </span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleRemoveVote(sheet.id)}
                                disabled={count === 0}
                                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-2 py-0.5 rounded text-xs font-bold transition-all"
                              >
                                −
                              </button>
                              <button
                                onClick={() => handleAddVote(sheet.id)}
                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-bold transition-all"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${colors.bg} transition-all duration-300`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Votes Details - Team by Team (hidden in SEQUENTIAL mode - use simplified view above) */}
      {votingMode !== 'SEQUENTIAL' && activeVotes.length > 0 ? (
        <div className="space-y-3">
          {activeVotes.map((vote: any) => {
            const colors = TEAM_COLORS[vote.teamColor] || { bg: 'bg-gray-600', text: 'text-gray-200' };
            const totalSubmissions = vote.submissions?.length || 0;

            // Get all sheets assigned to this team
            const teamAssignments = assignments.filter((a: any) => a.teamColor === vote.teamColor);
            const assignedSheetIds = teamAssignments.map((a: any) => a.sheetId);
            const assignedSheets = sheets.filter((s: any) => assignedSheetIds.includes(s.id));

            // Count votes per sheet
            const votesBySheet: Record<string, number> = {};
            if (vote.submissions) {
              vote.submissions.forEach((sub: any) => {
                const key = sub.sheetId || 'random';
                votesBySheet[key] = (votesBySheet[key] || 0) + 1;
              });
            }

            const handleAddVote = async (sheetId: string) => {
              try {
                const response = await fetch(`/api/sheet-votes/${vote.id}/add-manual-vote`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ optionType: 'sheet', sheetId }),
                });
                if (response.ok) {
                  await onRefresh();
                  toast.success('Voto aggiunto!');
                }
              } catch (error) {
                console.error('Error adding vote:', error);
                toast.error('Errore aggiunta voto');
              }
            };

            const handleRemoveVote = async (sheetId: string) => {
              try {
                // Find and remove one submission for this sheet
                const submission = vote.submissions?.find((s: any) => s.sheetId === sheetId);
                if (!submission) {
                  toast.error('Nessun voto da rimuovere');
                  return;
                }

                const response = await fetch(`/api/sheet-votes/submissions/${submission.id}`, {
                  method: 'DELETE',
                });
                if (response.ok) {
                  await onRefresh();
                  toast.success('Voto rimosso!');
                }
              } catch (error) {
                console.error('Error removing vote:', error);
                toast.error('Errore rimozione voto');
              }
            };

            const isActiveTeam = activeVotingTeam === vote.teamColor;
            const isCompleted = !vote.isActive && vote.closedAt;

            return (
              <div
                key={vote.id}
                className={`bg-gray-900 rounded-lg p-4 border ${
                  isActiveTeam
                    ? 'border-yellow-500 ring-2 ring-yellow-500/50'
                    : isCompleted
                    ? 'border-green-700'
                    : 'border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full ${colors.bg}`}></div>
                    <span className="text-white font-bold">Squadra {TEAM_NAMES[vote.teamColor as TeamColor] || vote.teamColor}</span>
                    {isActiveTeam && (
                      <span className="bg-yellow-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold animate-pulse">
                        ATTIVA
                      </span>
                    )}
                    {isCompleted && !isActiveTeam && (
                      <span className="bg-green-700 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                        COMPLETATA
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">{totalSubmissions} voti</span>
                    {!vote.isActive && !isCompleted && <span className="text-green-400 text-xs">✓ Chiusa</span>}
                  </div>
                </div>

                {/* Vote breakdown by ALL assigned heroes */}
                <div className="space-y-2">
                  {(() => {
                    // Find winner (highest vote count)
                    let maxVotes = 0;
                    let winnerSheetId: string | null = null;
                    assignedSheets.forEach((sheet: any) => {
                      const count = votesBySheet[sheet.id] || 0;
                      if (count > maxVotes) {
                        maxVotes = count;
                        winnerSheetId = sheet.id;
                      }
                    });

                    return assignedSheets.map((sheet: any) => {
                      const count = votesBySheet[sheet.id] || 0;
                      const percentage = totalSubmissions > 0 ? (count / totalSubmissions) * 100 : 0;
                      const assignment = teamAssignments.find((a: any) => a.sheetId === sheet.id);
                      const isWinner = isResultsMode && winnerSheetId === sheet.id && maxVotes > 0;

                      return (
                        <div
                          key={sheet.id}
                          className={`space-y-1 ${isWinner ? 'bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-2 -mx-2' : ''}`}
                        >
                          <div className="flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              {isWinner && <span className="text-yellow-400">🏆</span>}
                              <span className={isWinner ? 'text-yellow-300 font-bold' : 'text-gray-300'}>
                                {sheet.name}
                              </span>
                              {assignment?.isPrimary && !isWinner && (
                                <span className="text-yellow-400 text-xs">⭐</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={isWinner ? 'text-yellow-300 font-bold' : 'text-gray-400'}>
                                {count} ({percentage.toFixed(0)}%)
                              </span>
                              {!isResultsMode && (
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleRemoveVote(sheet.id)}
                                    disabled={count === 0}
                                    className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-2 py-0.5 rounded text-xs font-bold transition-all"
                                  >
                                    −
                                  </button>
                                  <button
                                    onClick={() => handleAddVote(sheet.id)}
                                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-bold transition-all"
                                  >
                                    +
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${isWinner ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : colors.bg} transition-all duration-300`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      ) : votingMode !== 'SEQUENTIAL' ? (
        <div className="text-center py-8 text-gray-400">
          {isResultsMode ? (
            <>
              <p>Nessun dato di voto disponibile</p>
              <p className="text-sm mt-1">I voti potrebbero essere stati cancellati</p>
            </>
          ) : (
            <>
              <p>Nessuna votazione attiva</p>
              <p className="text-sm mt-1">Clicca su "Vote" per iniziare</p>
            </>
          )}
        </div>
      ) : null}

      {votingMode !== 'SEQUENTIAL' && allClosed && !isResultsMode && (
        <div className="mt-4 p-3 bg-green-900/30 border border-green-600 rounded-lg">
          <p className="text-green-300 text-sm">
            ✓ Tutte le votazioni chiuse. Passa a "Vote Results" o "Winners"
          </p>
        </div>
      )}

      {votingMode !== 'SEQUENTIAL' && isResultsMode && activeVotes.length > 0 && (
        <div className="mt-4 p-3 bg-purple-900/30 border border-purple-600 rounded-lg">
          <p className="text-purple-300 text-sm">
            🏆 Risultati - Vincitori evidenziati sopra
          </p>
        </div>
      )}
    </div>
  );
}
